import React, { useState } from 'react';
import { Check, Star, Calendar, Clock, Shield, X, FileText } from 'lucide-react';

const DEPOSIT_CLAUSES = `
PRIMERA. OBJETO DE LA CESIÓN
El CEDENTE entrega al CESIONARIO, en régimen de cesión de uso (comodato), el dispositivo receptor de contenidos digitales denominado BRAVO (en adelante, el BIEN), incluyendo sus accesorios (fuente de alimentación y cableado), con la finalidad exclusiva de ser utilizado para la recepción de los contenidos distribuidos por Versión Digital S.L.

SEGUNDA. CONSTITUCIÓN DEL DEPÓSITO DE GARANTÍA
En el momento de la firma del presente documento, el CESIONARIO hace entrega al CEDENTE de la cantidad de Doscientos Cincuenta Euros (250,00 €) en concepto de Depósito de Garantía.

El CEDENTE acusa recibo de dicha cantidad mediante la firma de este documento.

Esta cantidad no constituye en ningún caso un pago por la compra del BIEN, el cual sigue siendo propiedad exclusiva del CEDENTE.

TERCERA. DURACIÓN DE LA CESIÓN
La cesión del BIEN estará vinculada a la vigencia del contrato de servicios de contenidos entre ambas partes, con una duración establecida de [MARCAR OPCIÓN: 1 AÑO / 3 AÑOS], prorrogable por mutuo acuerdo.

CUARTA. OBLIGACIONES DE CONSERVACIÓN
El CESIONARIO se obliga a:

• Mantener el BIEN en perfecto estado de conservación y funcionamiento.
• Utilizar el BIEN exclusivamente para los fines técnicos autorizados.
• No manipular, abrir ni intentar reparar el hardware del dispositivo sin autorización expresa del CEDENTE.
• Notificar de inmediato cualquier avería o mal funcionamiento.

QUINTA. CONDICIONES DE DEVOLUCIÓN DEL DEPÓSITO
Al término de la relación contractual o del periodo de cesión, el CEDENTE reintegrará al CESIONARIO la suma íntegra de 250,00 € sujeta al cumplimiento de las siguientes condiciones:

• Retorno Físico: El CESIONARIO deberá devolver el BIEN en las oficinas del CEDENTE en un plazo máximo de 15 días naturales tras el fin del contrato.
• Estado del Bien: El dispositivo debe devolverse en buen estado de funcionamiento, sin daños estéticos graves (más allá del desgaste ordinario por uso) y con todos sus accesorios originales.
• Verificación: El CEDENTE dispondrá de 7 días hábiles tras la recepción para verificar el estado técnico del equipo. Tras dicha verificación, procederá a la devolución del dinero mediante transferencia bancaria.

SEXTA. PÉRDIDA O DETERIORO DEL DEPÓSITO
El CESIONARIO perderá el derecho a la devolución total o parcial del depósito en los siguientes supuestos:

• No devolución del BIEN tras la finalización del contrato.
• Daños físicos irreparables causados por negligencia, uso indebido o manipulación no autorizada.
• Pérdida o robo del dispositivo. Si el coste de reposición o reparación del BIEN superase los 250,00 € depositados, el CEDENTE se reserva el derecho de reclamar la diferencia al CESIONARIO.

SÉPTIMA. JURISDICCIÓN Y FUERO
Para cualquier controversia que pudiera derivarse de la interpretación o ejecución del presente documento, las partes se someten a los Juzgados y Tribunales de la ciudad de Madrid, renunciando expresamente a cualquier otro fuero que pudiera corresponderles.
`;

const BravoPricingSection = ({ onScrollFadeIn }) => {
  const [showClausesModal, setShowClausesModal] = useState(false);

  const plans = [
    {
      id: 'event',
      name: 'Por Evento',
      icon: Calendar,
      price: '50',
      period: '/evento',
      description: 'Ideal para eventos puntuales',
      features: [
        'Alquiler por evento individual',
        'Sin compromiso de permanencia',
        'Soporte técnico incluido',
        'Configuración remota'
      ],
      recommended: false,
      commitment: null
    },
    {
      id: 'annual-2y',
      name: 'Anual Premium',
      icon: Star,
      price: '150',
      priceMonthly: '20',
      period: '/único pago',
      periodMonthly: '/mes',
      description: 'La mejor relación calidad-precio',
      features: [
        'Dispositivo en cesión permanente',
        'Soporte técnico prioritario 24/7',
        'Actualizaciones automáticas',
        'Acceso a catálogo completo',
        'Máximo ahorro a largo plazo'
      ],
      recommended: true,
      commitment: '2 años'
    },
    {
      id: 'annual-1y',
      name: 'Anual Flexible',
      icon: Clock,
      price: '195',
      priceMonthly: '25',
      period: '/único pago',
      periodMonthly: '/mes',
      description: 'Mayor flexibilidad contractual',
      features: [
        'Dispositivo en cesión permanente',
        'Soporte técnico incluido',
        'Actualizaciones automáticas',
        'Acceso a catálogo completo'
      ],
      recommended: false,
      commitment: '1 año'
    }
  ];

  return (
    <>
      <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(228,0,43,0.08)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(228,0,43,0.05)_0%,transparent_50%)]" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">Tarifas</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: '"Oswald", sans-serif' }}>
              CONSULTAR TARIFAS
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Elige la modalidad de alquiler que mejor se adapte a las necesidades de tu sala
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-xl overflow-hidden transition-all duration-500 hover:scale-[1.02] ${
                  plan.recommended 
                    ? 'bg-gradient-to-b from-[#e4002b]/20 to-[#1a1a1a] border-2 border-[#e4002b] shadow-[0_0_40px_rgba(228,0,43,0.3)]' 
                    : 'bg-[#1a1a1a] border border-white/10 hover:border-white/20'
                }`}
              >
                {/* Recommended Badge */}
                {plan.recommended && (
                  <div className="absolute top-0 left-0 right-0 bg-[#e4002b] py-2 text-center">
                    <span className="text-white text-xs font-bold uppercase tracking-widest">
                      Recomendado
                    </span>
                  </div>
                )}

                <div className={`p-8 ${plan.recommended ? 'pt-14' : ''}`}>
                  {/* Icon & Name */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      plan.recommended ? 'bg-[#e4002b]' : 'bg-white/10'
                    }`}>
                      <plan.icon className={`w-6 h-6 ${plan.recommended ? 'text-white' : 'text-[#e4002b]'}`} />
                    </div>
                    <div>
                      <h3 className="text-white text-xl font-bold" style={{ fontFamily: '"Oswald", sans-serif' }}>
                        {plan.name}
                      </h3>
                      {plan.commitment && (
                        <p className="text-white/50 text-sm">Compromiso {plan.commitment}</p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-white/60 text-sm mb-6">{plan.description}</p>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-white text-4xl font-bold">{plan.price}€</span>
                      <span className="text-white/50 text-sm">{plan.period}</span>
                    </div>
                    {plan.priceMonthly && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-white/40 text-sm">o</span>
                        <span className="text-white text-xl font-semibold">{plan.priceMonthly}€</span>
                        <span className="text-white/50 text-sm">{plan.periodMonthly}</span>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          plan.recommended ? 'text-[#e4002b]' : 'text-green-500'
                        }`} />
                        <span className="text-white/80 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <a
                    href="mailto:m.cazorla@versiondigital.es?subject=Solicitud Alquiler Bravo - Plan {plan.name}"
                    className={`block w-full py-3 rounded-lg text-center font-bold text-sm uppercase tracking-wider transition-all ${
                      plan.recommended
                        ? 'bg-[#e4002b] text-white hover:bg-[#c70024] shadow-lg'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Solicitar información
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Deposit Notice */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
              <Shield className="w-4 h-4 text-[#e4002b]" />
              <span className="text-white/60 text-sm">
                DEPÓSITO DE GARANTÍA POR CESIÓN DE EQUIPAMIENTO TÉCNICO: <span className="text-white font-semibold">250€</span>
              </span>
              <button
                onClick={() => setShowClausesModal(true)}
                className="text-[#e4002b] text-sm font-medium hover:underline ml-1"
                data-testid="deposit-terms-link"
              >
                consultar condiciones
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Clauses Modal */}
      {showClausesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowClausesModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-3xl max-h-[80vh] bg-[#1a1a1a] rounded-xl border border-white/10 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="sticky top-0 bg-[#1a1a1a] border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#e4002b]" />
                <h3 className="text-white text-lg font-bold" style={{ fontFamily: '"Oswald", sans-serif' }}>
                  CLÁUSULAS DEL DEPÓSITO DE GARANTÍA
                </h3>
              </div>
              <button
                onClick={() => setShowClausesModal(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-colors"
                data-testid="close-clauses-modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="prose prose-invert prose-sm max-w-none">
                {DEPOSIT_CLAUSES.split('\n\n').map((paragraph, idx) => {
                  // Check if it's a title (all caps or starts with numbered word)
                  const isTitle = /^[A-ZÁÉÍÓÚ]+\./.test(paragraph.trim());
                  
                  if (isTitle) {
                    return (
                      <h4 key={idx} className="text-[#e4002b] font-bold text-sm uppercase tracking-wider mt-6 mb-3">
                        {paragraph}
                      </h4>
                    );
                  }
                  
                  return (
                    <p key={idx} className="text-white/70 text-sm leading-relaxed mb-4 whitespace-pre-wrap">
                      {paragraph}
                    </p>
                  );
                })}
              </div>
            </div>
            
            {/* Footer */}
            <div className="sticky bottom-0 bg-[#1a1a1a] border-t border-white/10 px-6 py-4">
              <button
                onClick={() => setShowClausesModal(false)}
                className="w-full py-3 bg-[#e4002b] text-white font-bold rounded-lg hover:bg-[#c70024] transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BravoPricingSection;
